/**
 * Toast notification utility for consistent notifications across the application
 * Uses react-hot-toast for rendering toast messages
 *
 * IMPORTANT: This is not a true React hook - it's a factory function that returns
 * toast utility methods. It does NOT use any React hooks internally to avoid
 * the "Invalid hook call" errors caused by duplicate React instances.
 */

import { toast } from 'react-hot-toast';

// Default toast configuration
const defaultDuration = 3000; // 3 seconds
const defaultPosition = 'top-right';

/**
 * Factory function for toast utilities (not a true React hook)
 * @returns {Object} Object with methods for displaying different types of toasts
 */
export const useToast = () => {
  // Simple wrapper around toast functions - no hooks used internally
  return {
    /**
     * Show a success toast notification
     * @param {string} message - The message to display
     * @param {Object} options - Additional toast options
     */
    success: (message, options = {}) => {
      toast.success(message, {
        duration: defaultDuration,
        position: 'top-right',
        style: {
          background: '#10B981',
          color: '#FFFFFF',
          fontWeight: 'bold',
        },
        ...options,
      });
    },

    /**
     * Show an error toast notification
     * @param {string} message - The message to display
     * @param {Object} options - Additional toast options
     */
    error: (message, options = {}) => {
      toast.error(message, {
        duration: defaultDuration,
        position: 'top-right',
        style: {
          background: '#EF4444',
          color: '#FFFFFF',
          fontWeight: 'bold',
        },
        ...options,
      });
    },

    /**
     * Show an info toast notification
     * @param {string} message - The message to display
     * @param {Object} options - Additional toast options
     */
    info: (message, options = {}) => {
      toast(message, {
        duration: defaultDuration,
        position: 'top-right',
        style: {
          background: '#3B82F6',
          color: '#FFFFFF',
          fontWeight: 'bold',
        },
        ...options,
      });
    },

    /**
     * Show a warning toast notification
     * @param {string} message - The message to display
     * @param {Object} options - Additional toast options
     */
    warning: (message, options = {}) => {
      toast(message, {
        duration: defaultDuration,
        position: 'top-right',
        style: {
          background: '#F59E0B',
          color: '#FFFFFF',
          fontWeight: 'bold',
        },
        icon: '⚠️',
        ...options,
      });
    },

    /**
     * Dismiss all currently visible toasts
     */
    dismiss: () => {
      toast.dismiss();
    },
  };
};

export default useToast;
